import React, { useState, useEffect, useCallback } from 'react';
import { meterReadingsApi, MeterReadingType } from '../../api/meterReadingsApi';
import { useLang } from '../../contexts/LangContext';
import { generateBillingPeriods } from '../../utils/billingPeriods';
import { buildingsApi } from '../../api/buildingsApi';
import { apartmentsApi } from '../../api/apartmentsApi';
import { serviceFeeApi } from '../../api/serviceFeeApi';
import { feeCategoriesApi } from '../../api/feeCategoriesApi';
import { utilityMetersApi, UtilityMeter } from '../../api/utilityMetersApi';
import { getBillingModeLabel, getQuotaTierLabel } from '../../utils/quotaTierLabels';

interface AddUsageServiceFeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceType: string;
  feeCategory: any;
  apartmentId: string;
  buildingId?: string;
  onSave: (data: any) => Promise<void>;
}

const AddUsageServiceFeeModal: React.FC<AddUsageServiceFeeModalProps> = ({
  isOpen,
  onClose,
  serviceType,
  feeCategory,
  apartmentId,
  buildingId,
  onSave,
}) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [billingPeriod, setBillingPeriod] = useState(() => {
    const periods = generateBillingPeriods();
    return periods[0]?.value || '';
  });
  const [startReading, setStartReading] = useState<number>(0);
  const [endReading, setEndReading] = useState<number>(0);
  const [usage, setUsage] = useState('');
  const [amount, setAmount] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [registeredQuotaCount, setRegisteredQuotaCount] = useState<number | null>(null);
  const [tierBreakdown, setTierBreakdown] = useState<any[]>([]);
  const [utilityMeters, setUtilityMeters] = useState<UtilityMeter[]>([]);
  const [selectedMeterId, setSelectedMeterId] = useState('');

  const [buildingSettings, setBuildingSettings] = useState<any>(null);

  const billingPeriods = generateBillingPeriods();
  const selectedMeter = utilityMeters.find((m) => m.id === selectedMeterId);

  const getMeterType = useCallback((): MeterReadingType | null => {
    const feeName = (feeCategory?.feeCategory?.name || serviceType || '').toLowerCase();
    if (feeName.includes('điện')) return MeterReadingType.ELECTRICITY;
    if (feeName.includes('nước')) return MeterReadingType.WATER;
    return null;
  }, [feeCategory, serviceType]);

  const getUsageUnit = () => {
    const feeName = (feeCategory?.feeCategory?.name || serviceType || '').toLowerCase();
    if (feeName.includes('điện')) return 'kWh';
    if (feeName.includes('nước')) return 'm³';
    if (feeCategory?.feeCategory?.feeTiers?.[0]?.unit) {
      return feeCategory.feeCategory.feeTiers[0].unit;
    }
    return 'đơn vị';
  };

  const loadBuildingSettings = async () => {
    if (!apartmentId) return;

    try {
      const apartment = await apartmentsApi.getApartmentById(apartmentId);
      setRegisteredQuotaCount(apartment.registeredQuotaCount ?? null);
      const building = await buildingsApi.getBuilding(apartment.buildingId);
      setBuildingSettings(building);
    } catch (err) {
      console.error('Failed to load building settings:', err);
    }
  };

  const loadUtilityMeters = async () => {
    const meterType = getMeterType();
    if (!meterType || !buildingId) {
      setUtilityMeters([]);
      setSelectedMeterId('');
      return;
    }

    try {
      const allMeters = await utilityMetersApi.listAll({ buildingId, type: meterType });
      const apartmentMeters = allMeters.filter((m) => m.apartmentId === apartmentId);
      setUtilityMeters(apartmentMeters);
      if (apartmentMeters.length === 1) {
        setSelectedMeterId(apartmentMeters[0].id);
      } else {
        setSelectedMeterId('');
      }
    } catch (err) {
      console.error('Failed to load utility meters:', err);
      setUtilityMeters([]);
      setSelectedMeterId('');
    }
  };

  const loadPreviousReading = async () => {
    if (!apartmentId || !feeCategory?.feeCategoryId || !billingPeriod) return;

    try {
      const previousReading = await serviceFeeApi.getPreviousReading(
        apartmentId,
        feeCategory.feeCategoryId,
        billingPeriod,
        selectedMeterId || undefined,
      );

      if (previousReading !== null && previousReading !== undefined) {
        setStartReading(previousReading);
      } else {
        setStartReading(0);
      }
    } catch (err) {
      console.error('Failed to load previous reading:', err);
      setStartReading(0);
    }
  };

  const calculateUsageAmount = async (calculatedUsage: number) => {
    if (!feeCategory?.feeCategory) {
      return;
    }

    const fee = feeCategory.feeCategory;
    let totalAmount = 0;

    if (fee.pricingType === 'SINGLE_TIER') {
      const basePrice = fee.basePrice || 0;
      const baseAmount = calculatedUsage * basePrice;
      const vatAmount = baseAmount * (fee.vatPercentage / 100);
      totalAmount = baseAmount + vatAmount;
      setTierBreakdown([]);
    } else if (fee.pricingType === 'TIERED') {
      const calculationResult = await feeCategoriesApi.calculateTieredFee(
        feeCategory.feeCategoryId,
        calculatedUsage,
        apartmentId,
        selectedMeterId || undefined,
      );
      totalAmount = calculationResult.totalAmount || 0;
      setTierBreakdown(calculationResult.scaledTiers || []);
      if (calculationResult.registeredQuotaCount) {
        setRegisteredQuotaCount(calculationResult.registeredQuotaCount);
      }
    } else if (fee.pricingType === 'QUOTA_BASED') {
      const calculationResult = await feeCategoriesApi.calculateQuotaBasedFee(
        feeCategory.feeCategoryId,
        calculatedUsage,
        'RESIDENT',
        'RESIDENT',
        apartmentId,
      );
      totalAmount = calculationResult.totalAmount || 0;
      setTierBreakdown([]);
    }

    setAmount(Math.round(totalAmount).toString());
  };

  const calculateAmount = async () => {
    const diffUsage = endReading - startReading;

    if (diffUsage >= 0) {
      let calculatedUsage = diffUsage;
      if (feeCategory && feeCategory.feeCategory) {
        const fee = feeCategory.feeCategory;
        const feeName = (fee.name || '').toLowerCase();
        const unit = (fee.feeTiers && fee.feeTiers[0]?.unit ? String(fee.feeTiers[0].unit) : '').toLowerCase();
        const manualUsage = parseFloat(usage || '');
        if (fee.pricingType === 'TIERED' && (unit.includes('xe') || feeName.includes('gửi xe'))) {
          if (!Number.isNaN(manualUsage) && manualUsage > 0) {
            calculatedUsage = manualUsage;
          }
        }
      }
      const roundedUsage = Math.round(calculatedUsage * 1000) / 1000;
      setUsage(roundedUsage.toString());

      if (feeCategory?.feeCategory) {
        try {
          await calculateUsageAmount(calculatedUsage);
        } catch (calcError) {
          console.error('Error calculating usage amount:', calcError);
          const rate = serviceType.includes('nước') ? 6000 : serviceType.includes('điện') ? 3500 : 2000;
          setAmount((calculatedUsage * rate).toString());
        }
      } else {
        const rate = serviceType.includes('nước') ? 6000 : serviceType.includes('điện') ? 3500 : 2000;
        setAmount((calculatedUsage * rate).toString());
      }
    }
  };

  const calculateDateRanges = () => {
    if (!buildingSettings || !billingPeriod) {
      return;
    }

    try {
      const periodMatch = billingPeriod.match(/(\d{2})\/(\d{4})/);
      if (!periodMatch) {
        return;
      }

      const [, month, year] = periodMatch;
      const monthNum = parseInt(month);
      const yearNum = parseInt(year);

      if (isNaN(monthNum) || isNaN(yearNum) || monthNum < 1 || monthNum > 12) {
        return;
      }

      const fromDay = Math.max(1, Math.min(31, buildingSettings.paymentFromDay || 10));
      const fromDateObj = new Date(Date.UTC(yearNum, monthNum - 1, fromDay));

      const toDay = Math.max(1, Math.min(31, buildingSettings.paymentToDay || 20));
      const dueDateObj = new Date(Date.UTC(yearNum, monthNum - 1, toDay));

      if (isNaN(fromDateObj.getTime()) || isNaN(dueDateObj.getTime())) {
        return;
      }

      setFromDate(fromDateObj.toISOString().split('T')[0]);
      setDueDate(dueDateObj.toISOString().split('T')[0]);
    } catch (dateError) {
      console.error('Error calculating date ranges:', dateError);
    }
  };

  useEffect(() => {
    if (isOpen && apartmentId) {
      loadBuildingSettings();
      loadUtilityMeters();
    }
  }, [isOpen, apartmentId, buildingId, feeCategory, serviceType]);

  useEffect(() => {
    if (buildingSettings && billingPeriod) {
      calculateDateRanges();
    }
  }, [buildingSettings, billingPeriod]);

  useEffect(() => {
    if (isOpen && apartmentId && feeCategory && billingPeriod) {
      loadPreviousReading();
    }
  }, [isOpen, apartmentId, feeCategory, billingPeriod, selectedMeterId]);

  useEffect(() => {
    if (isOpen && feeCategory && startReading >= 0 && endReading >= 0) {
      calculateAmount();
    }
  }, [isOpen, feeCategory, startReading, endReading, selectedMeterId]);

  useEffect(() => {
    if (!isOpen || !apartmentId || !feeCategory) return;
    const meterType = getMeterType();
    if (!meterType) return;

    const periodForPrev = billingPeriod || generateBillingPeriods()[0]?.value || '';
    meterReadingsApi
      .getPreviousReading(apartmentId, meterType, periodForPrev, selectedMeterId || undefined)
      .then(async (prev) => {
        if (prev && prev > 0) {
          setStartReading(prev);
          return;
        }
        if (buildingId) {
          try {
            const last = await meterReadingsApi.getLastReadings(buildingId, meterType);
            const found = last.find((r: any) =>
              selectedMeterId
                ? r.utilityMeterId === selectedMeterId
                : r.apartmentId === apartmentId,
            );
            if (found && typeof found.newReading === 'number') {
              setStartReading(found.newReading);
            }
          } catch {
            // ignore fallback errors
          }
        }
      })
      .catch(async () => {
        if (buildingId) {
          try {
            const last = await meterReadingsApi.getLastReadings(buildingId, meterType);
            const found = last.find((r: any) =>
              selectedMeterId
                ? r.utilityMeterId === selectedMeterId
                : r.apartmentId === apartmentId,
            );
            if (found && typeof found.newReading === 'number') {
              setStartReading(found.newReading);
            }
          } catch {
            // ignore fallback errors
          }
        }
      });
  }, [isOpen, apartmentId, feeCategory, billingPeriod, buildingId, selectedMeterId, getMeterType]);

  const handleReadingChange = async (type: 'start' | 'end', value: string) => {
    const numValue = parseFloat(value) || 0;

    let newStartReading = startReading;
    let newEndReading = endReading;

    if (type === 'start') {
      setStartReading(numValue);
      newStartReading = numValue;
    } else {
      setEndReading(numValue);
      newEndReading = numValue;
    }

    const calculatedUsage = newEndReading - newStartReading;

    if (calculatedUsage >= 0) {
      const roundedUsage = Math.round(calculatedUsage * 1000) / 1000;
      setUsage(roundedUsage.toString());

      if (feeCategory?.feeCategory) {
        try {
          await calculateUsageAmount(calculatedUsage);
        } catch (calcError) {
          console.error('Error calculating usage amount:', calcError);
          const rate = serviceType.includes('nước') ? 6000 : serviceType.includes('điện') ? 3500 : 2000;
          setAmount((calculatedUsage * rate).toString());
        }
      } else {
        const rate = serviceType.includes('nước') ? 6000 : serviceType.includes('điện') ? 3500 : 2000;
        setAmount((calculatedUsage * rate).toString());
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const meterType = getMeterType();
    if (meterType && utilityMeters.length > 0 && !selectedMeterId) {
      setError('Vui lòng chọn mã đồng hồ');
      return;
    }

    setLoading(true);

    try {
      const data = {
        apartmentId,
        feeCategoryId: feeCategory?.feeCategoryId,
        billingPeriod,
        startReading: parseFloat(startReading.toString()) || 0,
        endReading: parseFloat(endReading.toString()) || 0,
        usage: parseFloat(usage) || 0,
        amount: parseFloat(amount) || 0,
        utilityMeterId: selectedMeterId || undefined,
        fromDate,
        dueDate,
        serviceType,
        type: 'USAGE_BASED',
      };

      await onSave(data);
      onClose();
      setStartReading(0);
      setEndReading(0);
      setUsage('');
      setAmount('');
      setSelectedMeterId('');
      setUtilityMeters([]);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Có lỗi xảy ra khi thêm hóa đơn');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    const periods = generateBillingPeriods();
    setBillingPeriod(periods[0]?.value || '');
    setStartReading(0);
    setEndReading(0);
    setUsage('');
    setAmount('');
    setFromDate('');
    setDueDate('');
    setTierBreakdown([]);
    setUtilityMeters([]);
    setSelectedMeterId('');
    setError(null);
    onClose();
  };

  const renderMeterBillingHint = () => {
    if (!selectedMeter) {
      if (getMeterType() && utilityMeters.length > 1) {
        return (
          <p className="mt-1 text-xs text-amber-600">
            Chọn mã đồng hồ để tính phí theo đúng bậc giá của đồng hồ
          </p>
        );
      }
      if (serviceType.toLowerCase().includes('nước') && registeredQuotaCount != null && registeredQuotaCount > 0) {
        return (
          <p className="mt-1 text-xs text-blue-600">
            Số lượng ĐKĐM: {registeredQuotaCount} — bậc thang được nhân theo định mức đăng ký
          </p>
        );
      }
      if (serviceType.toLowerCase().includes('nước')) {
        return (
          <p className="mt-1 text-xs text-amber-600">
            Chưa đăng ký ĐKĐM — toàn bộ m³ sẽ tính theo một bậc giá tương ứng mức sử dụng
          </p>
        );
      }
      return null;
    }

    if (selectedMeter.billingMode === 'NON_QUOTA') {
      return (
        <p className="mt-1 text-xs text-blue-600">
          {selectedMeter.meterCode}: {getBillingModeLabel(selectedMeter.billingMode, t)} — tính toàn bộ theo{' '}
          {getQuotaTierLabel(selectedMeter.quotaTierLevel ?? selectedMeter.quotaTierStart, t)}
        </p>
      );
    }

    return (
      <p className="mt-1 text-xs text-blue-600">
        {selectedMeter.meterCode}: {getBillingModeLabel(selectedMeter.billingMode, t)}
        {selectedMeter.feeMultiplier && selectedMeter.feeMultiplier > 0
          ? ` — hệ số ${selectedMeter.feeMultiplier}`
          : ''}
      </p>
    );
  };

  if (!isOpen) return null;

  const usageUnit = getUsageUnit();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Thêm hóa đơn dịch vụ
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <div className="bg-green-100 border border-green-400 text-green-700 px-3 py-2 rounded text-sm">
            {serviceType} (phí theo số sử dụng thực tế)
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Loại phí dịch vụ
            </label>
            <input
              type="text"
              value={serviceType}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
            />
          </div>

          {getMeterType() && utilityMeters.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mã đồng hồ
              </label>
              <select
                value={selectedMeterId}
                onChange={(e) => setSelectedMeterId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required={utilityMeters.length > 0}
              >
                {utilityMeters.length > 1 && <option value="">-- Chọn đồng hồ --</option>}
                {utilityMeters.map((meter) => (
                  <option key={meter.id} value={meter.id}>
                    {meter.meterCode} — {getQuotaTierLabel(meter.quotaTierLevel ?? meter.quotaTierStart, t)} (
                    {getBillingModeLabel(meter.billingMode, t)})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kỳ hóa đơn
            </label>
            <select
              value={billingPeriod}
              onChange={(e) => setBillingPeriod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              {billingPeriods.map((period) => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Số đầu kỳ
            </label>
            <input
              type="number"
              value={startReading.toString()}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Số đầu kỳ"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Số cuối kỳ
            </label>
            <input
              type="number"
              value={endReading.toString()}
              onChange={(e) => handleReadingChange('end', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Số cuối kỳ"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Số sử dụng
            </label>
            <input
              type="number"
              value={usage}
              onChange={(e) => setUsage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Số sử dụng"
              readOnly
            />
            {renderMeterBillingHint()}
          </div>

          {tierBreakdown.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-gray-700 space-y-1">
              <div className="font-medium text-blue-800">Chi tiết bậc thang</div>
              {tierBreakdown.map((tier) => (
                <div key={tier.tierLevel}>
                  Bậc {tier.tierLevel}: {tier.usageInTier} {usageUnit} × {tier.unitPrice?.toLocaleString('vi-VN')} = {Math.round(tier.amount || 0).toLocaleString('vi-VN')} VND
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Thành tiền
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Số tiền"
              min="0"
              step="1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hạn thanh toán từ ngày
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Đến ngày
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
            >
              {loading ? 'Đang lưu...' : 'Lưu'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUsageServiceFeeModal;
